import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { finalize, map } from 'rxjs';

import { StorageService } from './storage.service';
import { AuthStoreService } from './auth-store.service';
import { PropertySummary } from '@app/core/models';
import { PropertiesApiService } from '@app/features/properties/properties-api.service';
import { AccountContextService } from './account-context.service';


@Injectable({ providedIn: 'root' })
export class PropertyContextService {
  private readonly propertiesApi = inject(PropertiesApiService);
  private readonly authStore = inject(AuthStoreService);
  private readonly storage = inject(StorageService);
  private readonly accountContext = inject(AccountContextService);

  private readonly propertiesState = signal<PropertySummary[]>([]);
  private readonly selectedPropertyIdState = signal<string | null>(null);
  private readonly loadingState = signal(false);
  private loadRequestVersion = 0;
  private readonly contextUser = signal<unknown>(null);
  private readonly contextAccountId = signal<string | null>(null);
  private readonly isCurrentContext = computed(() =>
    this.contextUser() === this.authStore.user() &&
    this.contextAccountId() === this.accountContext.selectedAccountId() &&
    !this.accountContext.loading()
  );

  readonly properties = computed(() => this.isCurrentContext() ? this.propertiesState() : []);
  readonly selectedPropertyId = computed(() => this.isCurrentContext() ? this.selectedPropertyIdState() : null);
  readonly selectedProperty = computed(
    () =>
      this.properties().find((property) => property.id === this.selectedPropertyId()) ?? null
  );
  readonly loading = computed(() => this.loadingState());

  constructor() {
    effect(() => {
      const user = this.authStore.user();
      this.accountContext.selectedAccountId();
      this.accountContext.loading();

      if (!user) {
        untracked(() => this.reset());
        return;
      }

      untracked(() => this.loadPropertiesForUser());
    }, { allowSignalWrites: true });
  }

  loadProperties(preferredPropertyId?: string): void {
    const user = this.authStore.user();
    if (!user) {
      this.reset();
      return;
    }

    if (preferredPropertyId) {
      this.storage.setSelectedPropertyId(preferredPropertyId);
    }

    this.loadPropertiesForUser();
  }

  reset(): void {
    this.loadRequestVersion += 1;
    this.contextUser.set(null);
    this.contextAccountId.set(null);
    this.propertiesState.set([]);
    this.selectedPropertyIdState.set(null);
    this.loadingState.set(false);
  }

  private loadPropertiesForUser(): void {
    const user = this.authStore.user();
    const selectedAccountId = this.accountContext.selectedAccountId();
    if (!user || this.accountContext.loading() || (this.authStore.role() === 'partner' && !selectedAccountId)) {
      this.reset();
      return;
    }

    const requestVersion = ++this.loadRequestVersion;
    const isCurrent = () => requestVersion === this.loadRequestVersion && user === this.authStore.user() &&
      selectedAccountId === this.accountContext.selectedAccountId() && !this.accountContext.loading();
    if (!this.isCurrentContext()) {
      this.propertiesState.set([]);
      this.selectedPropertyIdState.set(null);
    }
    this.contextUser.set(user);
    this.contextAccountId.set(selectedAccountId);
    this.loadingState.set(true);
    const propertiesRequest = selectedAccountId
      ? this.accountContext.getProperties(selectedAccountId).pipe(
          // The account-context contract intentionally returns only safe selector fields.
          map((properties) => properties.map((property): PropertySummary => ({
            id: property.propertyId,
            name: property.displayName,
            code: property.code,
            complianceStatus: 'ON_TRACK',
            lifecycleStatus: 'ACTIVE',
            onboardingStatus: 'VALIDATED',
            marketTag: property.pmsProvider || 'Property',
            lastSyncAt: ''
          })))
        )
      : this.propertiesApi.getProperties();

    propertiesRequest
      .pipe(finalize(() => {
        if (isCurrent()) {
          this.loadingState.set(false);
        }
      }))
      .subscribe({
        next: (properties) => {
          if (!isCurrent()) {
            return;
          }

          this.propertiesState.set(properties);

          const persisted = this.storage.getSelectedPropertyId();

          const selectedProperty =
            properties.find((property) => property.id === persisted) ??
            properties[0] ??
            null;

          if (selectedProperty) {
            this.selectedPropertyIdState.set(selectedProperty.id);
            this.storage.setSelectedPropertyId(selectedProperty.id);
            return;
          }

          this.selectedPropertyIdState.set(null);
          this.storage.clearSelectedPropertyId();
        },
        error: () => {
          if (!isCurrent()) {
            return;
          }

          this.reset();
          if (selectedAccountId) {
            this.accountContext.loadAccounts().subscribe();
          }
        }
      });
  }

  selectProperty(propertyId: string): void {

    if (!propertyId || !this.properties().some((property) => property.id === propertyId)) {
      return;
    }

    this.selectedPropertyIdState.set(propertyId);
    this.storage.setSelectedPropertyId(propertyId);
  }
}
